import React, { useState, useEffect, useCallback } from 'react';
import {
  categoriesApi, accountsApi, goalsApi, incomesApi, transactionsApi,
  membersApi, savingsPlansApi, debtPlansApi, spendingGoalsApi, householdSettingsApi,
} from '../services/api';
import { fmt, groupBy } from '../utils/format';
import {
  computeRetirementTargets, computeStrategy, computeEffectiveSavings, accountBalance,
  savingsPlanMonthlyTarget,
} from '../utils/calculations';
import { classifyCategories, ownedCategoryNames } from '../utils/categoryClass';
import ProjectionsPanel from '../components/ProjectionsPanel';
import { useUnsavedFlag } from '../context/UnsavedChanges';

// Render order for the "other monthly" (non-cut) category groups.
const OTHER_GROUP_ORDER = [
  'House costs', 'Utilities', 'Insurance', 'Subscriptions', 'Credit Card',
  'Non-grocery foods', 'Misc. Variable', 'Savings/Investments', 'Debt Payments',
];

// Defined at module scope (NOT inside the component) so their identity is stable
// across renders — otherwise React remounts the <input> on every keystroke and the
// cursor jumps out after each character.
const InlineNum = ({ value, onChange, width = 95 }) => (
  <input className="field-input" type="number" step="0.01"
    style={{ width, textAlign: 'right', fontSize: 12, padding: '2px 6px' }}
    value={typeof value === 'number' ? parseFloat(value.toFixed(2)) : (value ?? '')}
    onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)} />
);

const ToggleBtn = ({ on, onClick, style }) => (
  <button onClick={onClick}
    style={{
      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, minWidth: 30,
      border: `1px solid ${on ? 'var(--green)' : 'var(--border2)'}`,
      background: on ? 'var(--green-bg)' : 'transparent',
      color: on ? 'var(--green)' : 'var(--text3)',
      cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em', ...style,
    }}>
    {on ? 'ON' : 'OFF'}
  </button>
);

export default function StrategyTab({ month, onRefresh }) {
  const [cats,     setCats]     = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [txs, setTxs] = useState([]);
  const [goals,    setGoals]    = useState([]);
  const [members,  setMembers]  = useState([]);
  const [savings,  setSavings]  = useState([]);
  const [debts,    setDebts]    = useState([]);
  const [spending, setSpending] = useState([]);
  const [settings, setSettings] = useState(null);
  const [incomes,  setIncomes]  = useState([]);

  const [newGoal,       setNewGoal]       = useState('');
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [editingText,   setEditingText]   = useState('');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [showProjections, setShowProjections] = useState(false);

  // Fixed category edits (by category id)
  const [fixedEdits, setFixedEdits] = useState({});

  // "The numbers on screen no longer match what the Budget tab has stored."
  // Declared with the other state so load() can reset it.
  const [unsaved, setUnsaved] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      categoriesApi.getAll(month.id),
      accountsApi.getAll(month.id),
      goalsApi.getAll(),
      membersApi.getAll(),
      savingsPlansApi.getAll(),
      debtPlansApi.getAll(),
      spendingGoalsApi.getAll(),
      householdSettingsApi.get(),
      incomesApi.get(month.id),
      // Needed to value accounts that are set to Live.
      transactionsApi.getAll(month.id),
    ]).then(([cR, aR, gR, mR, sR, dR, spR, hsR, iR, tR]) => {
      setCats(cR.data); setAccounts(aR.data); setGoals(gR.data);
      setMembers(mR.data); setSavings(sR.data); setDebts(dR.data);
      setSpending(spR.data); setSettings(hsR.data); setIncomes(iR.data);
      setTxs(tR.data);
      setFixedEdits({}); setUnsaved(false);
      setLoading(false);
    });
  }, [month.id]);

  useEffect(() => { load(); }, [load]);

  // ── Local edit helpers ─────────────────────────────────────────────────────
  //
  // Everything below moves the computed plan, and only Save all writes it into
  // the categories — including the toggles, which persist the plan's own
  // enabled state immediately but do NOT rewrite category planned amounts.
  // That gap is what made Strategy and Budget silently disagree.
  const touch = () => setUnsaved(true);

  const editSpending = (id, monthlyAmount) => { touch();
    setSpending(prev => prev.map(g => g.id === id ? { ...g, monthlyAmount } : g)); };

  // Toggles persist immediately — they're plan state, not month state
  const toggleSavings = (p) => {
    touch();
    const next = { ...p, enabled: !p.enabled, monthlyOverride: null };
    setSavings(prev => prev.map(x => x.id === p.id ? next : x));
    savingsPlansApi.update(p.id, next);
  };
  const toggleDebt = (p) => {
    touch();
    const next = { ...p, enabled: !p.enabled };
    setDebts(prev => prev.map(x => x.id === p.id ? next : x));
    debtPlansApi.update(p.id, next);
  };
  const allSavingsOn = savings.length > 0 && savings.every(p => p.enabled);
  const toggleAllSavings = () => {
    touch();
    const next = !allSavingsOn;
    setSavings(prev => prev.map(p => ({ ...p, enabled: next, monthlyOverride: null })));
    savings.forEach(p => savingsPlansApi.update(p.id, { ...p, enabled: next, monthlyOverride: null }));
  };

  const handleFixedEdit  = (catId, val) => { touch(); setFixedEdits(p => ({ ...p, [catId]: val })); };
  const getFixedPlanned  = (cat) => fixedEdits[cat.id] !== undefined ? fixedEdits[cat.id] : (cat.planned || 0);

  // ── Goals checklist (unchanged) ────────────────────────────────────────────
  const handleAddGoal = async () => { if (!newGoal.trim()) return; await goalsApi.add({ description: newGoal.trim() }); setNewGoal(''); load(); };
  const handleDelGoal = async (id) => { await goalsApi.delete(id); load(); };
  const handleTogGoal = async (g)  => { await goalsApi.update(g.id, { completed: !g.completed }); load(); };
  const handleSaveGoalEdit = async (g) => {
    const trimmed = editingText.trim();
    if (trimmed && trimmed !== g.description) {
      await goalsApi.update(g.id, { description: trimmed });
      load();
    }
    setEditingGoalId(null);
  };

  useUnsavedFlag('Strategy', unsaved);

  if (loading || !settings) return <div className="spinner" />;

  // ── Strategy calculation ───────────────────────────────────────────────────

  const incomeByMemberId = {};
  for (const r of incomes) {
    if (r.memberId != null)
      incomeByMemberId[r.memberId] = (incomeByMemberId[r.memberId] || 0) + (parseFloat(r.amount) || 0);
  }
  const grossIncome = incomes.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  /**
   * Balance of a linked account, valued the way the Accounts tab shows it
   * (ledger when Live, stored balance when Manual). Returns null when the link
   * points at an account that no longer exists, so the caller can fall back to
   * the plan's own stored figure rather than silently reading 0.
   */
  const linkedAccountBalance = (name) => {
    if (!name) return null;
    const a = accounts.find(x => x.name === name);
    if (!a) return null;
    return accountBalance(a, txs);
  };

  // When a plan is linked to an account, the account balance is the source of
  // truth — updating it from a statement is what moves the payoff date. Debt
  // accounts are stored negative, so take the magnitude for principal.
  const withLinkedBalances = (plans, field, useMagnitude) => plans.map(p => {
    const bal = linkedAccountBalance(p.linkedAccountName);
    return bal == null ? p : { ...p, [field]: useMagnitude ? Math.abs(bal) : bal };
  });

  const savingsResolved = withLinkedBalances(savings, 'balance', false);
  const debtsResolved   = withLinkedBalances(debts, 'principal', true);

  const calcTargets = computeRetirementTargets({
    members, savingsPlans: savingsResolved, debtPlans: debtsResolved, settings, incomes,
  });

  // Per-plan base amount comes from Setup details (balance, rate, target, dates).
  const baseOf = (p) => savingsPlanMonthlyTarget(p, calcTargets);

  // Debt: base payment (incl. escrow) + payoff-date extra.
  // toggleFullPayment plans feed their WHOLE payment into the toggle system;
  // mortgage-style plans keep the base in fixed costs and toggle only the extra.
  const debtBase  = (p) => (p.baseMonthlyPmt || 0) + (p.escrow || 0);
  const debtExtra = (p) => calcTargets.debtTargets[p.id]?.extraGoal || 0;
  const debtToggleAmt = (p) => p.toggleFullPayment ? debtBase(p) + debtExtra(p) : debtExtra(p);

  // A category belongs to exactly one arm of the cut, decided by what in Setup
  // points at it. Everything Setup claims — savings plan, debt plan, OR spending
  // goal — is already represented by that arm's own total, so it must not also
  // appear in the fixed total. Leaving spending goals out of this set is what
  // used to count a goal-linked envelope twice: once at its planned amount as a
  // fixed cost, and again at its goal amount as a variable one.
  const ownedNames = ownedCategoryNames({
    spendingGoals: spending, savingsPlans: savings, debtPlans: debts });

  // Everything Setup does NOT claim is an "other monthly" fixed cost. Each gets
  // an editable Planned amount here so nothing is left uncalculated, and they
  // all count toward the deficit. Cuts never touch this group.
  const fixedCats = cats.filter(c => !ownedNames.has(c.name));

  // Two things in Setup pointing at one envelope. The money is still counted
  // once (in the winning arm), but Save all can only write one planned amount,
  // so say so rather than letting the last writer quietly win.
  const catClasses = classifyCategories({
    categories: cats, spendingGoals: spending, savingsPlans: savings, debtPlans: debts });
  const conflictedCats = cats.filter(c => catClasses.get(c.name)?.conflict);

  const fixedByGroup = groupBy(fixedCats, 'groupName');
  const fixedGroupKeys = Object.keys(fixedByGroup).sort((a, b) => {
    const ai = OTHER_GROUP_ORDER.indexOf(a), bi = OTHER_GROUP_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1; if (bi === -1) return -1;
    return ai - bi;
  });
  const fixedDebtBase = debts.filter(p => !p.toggleFullPayment).reduce((s, p) => s + debtBase(p), 0);
  const fixedTotal = fixedCats.reduce((s, c) => s + (parseFloat(getFixedPlanned(c)) || 0), 0) + fixedDebtBase;

  const varBaseTotal = spending.reduce((s, g) => s + (parseFloat(g.monthlyAmount) || 0), 0);

  const { effSavingsByPlanId, effDebtExtraByPlanId, varScale } = computeEffectiveSavings({
    savingsPlans: savings.map(p => ({ id: p.id, type: p.type, ownerMemberId: p.ownerMemberId, enabled: !!p.enabled, baseAmount: baseOf(p) })),
    debtPlans:    debts.map(p => ({ id: p.id, enabled: !!p.enabled, extraGoal: debtToggleAmt(p) })),
    incomeByMemberId, varBaseTotal,
    max401kIncomePct: settings.max401kIncomePct ?? 0.80,
  });
  const allSavingsEffOff = savings.reduce((s, p) => s + (effSavingsByPlanId[p.id] || 0), 0) < 0.01;

  const strat = computeStrategy({
    incomes: incomes.map(r => ({ memberId: r.memberId, amount: parseFloat(r.amount) || 0 })),
    members,
    fixedTotal,
    spendingGoals:  spending.map(g => ({ id: g.id, amount: (parseFloat(g.monthlyAmount) || 0) * varScale })),
    savingsAmounts: savings.map(p => ({ id: p.id, amount: effSavingsByPlanId[p.id] || 0 })),
    debtExtras:     debts.map(p => ({ id: p.id, amount: effDebtExtraByPlanId[p.id] || 0 })),
  });

  const sc = 1 - strat.savingsCut;
  const dc = 1 - strat.debtCut;
  const vc = 1 - strat.variableCut;

  // Final planned amount per debt plan (what gets written to its category)
  const debtPlanned = (p) => p.toggleFullPayment
    ? (effDebtExtraByPlanId[p.id] || 0) * dc
    : debtBase(p) + (effDebtExtraByPlanId[p.id] || 0) * dc;

  const pct = (n) => n === 0 ? '—' : `${(n * 100).toFixed(1)}%`;

  // ── Save all ───────────────────────────────────────────────────────────────

  const handleSaveAll = async () => {
    setSaving(true);
    // Income is not written from here any more — it mirrors the members' salaries
    // in Setup, which is the only place it's entered.
    await Promise.all(debts.map(p => debtPlansApi.update(p.id, p)));
    await Promise.all(spending.map(g => spendingGoalsApi.update(g.id, {
      ...g, monthlyAmount: parseFloat(g.monthlyAmount) || 0,
    })));
    await Promise.all(
      Object.entries(fixedEdits).map(([id, planned]) =>
        categoriesApi.update(Number(id), { planned: parseFloat(planned) || 0 })
      )
    );
    // Single source of truth: write every linked category's post-cut planned amount
    const catPlanned = [
      ...spending.filter(g => g.linkedCategoryName).map(g => ({
        name: g.linkedCategoryName,
        planned: (parseFloat(g.monthlyAmount) || 0) * varScale * vc,
      })),
      ...savings.filter(p => p.linkedCategoryName).map(p => ({
        name: p.linkedCategoryName,
        planned: (effSavingsByPlanId[p.id] || 0) * sc,
      })),
      ...debts.filter(p => p.linkedCategoryName).map(p => ({
        name: p.linkedCategoryName,
        planned: debtPlanned(p),
      })),
    ];
    await Promise.all(
      catPlanned.map(({ name, planned }) => {
        const cat = cats.find(c => c.name === name);
        if (!cat) return Promise.resolve();
        return categoriesApi.update(cat.id, { planned: Math.round(planned * 100) / 100 });
      })
    );
    setSaving(false); setSavedMsg(true); setUnsaved(false);
    setTimeout(() => setSavedMsg(false), 2500);
    load(); onRefresh();
  };

  // ── UI bits ────────────────────────────────────────────────────────────────

  const groupHdr = (label) => (
    <tr style={{ background: 'var(--surface2)', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      <td colSpan={4} style={{ padding: '6px 10px' }}>{label}</td>
    </tr>
  );

  // Loans are liabilities (stored negative); normalise the sign so a mis-entered
  // positive balance still counts as debt and is listed, instead of cancelling out
  // James's loan and silently vanishing from the card.
  const loanAccts = accounts.filter(a => a.accountType === 'Loan');
  const totalDebtBalance = loanAccts.reduce((s, a) => s - Math.abs(a.balance), 0);
  const k401kPlans = savings.filter(p => p.type === 'RETIREMENT_401K');

  return (
    <>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>

      {/* ── LEFT ─────────────────────────────────────────────── */}
      <div>

        {/* Income */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span className="sec-label" style={{ margin: 0 }}>Income</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {savedMsg && <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 700 }}>✓ Saved</span>}
              {unsaved && !savedMsg && (
                <span style={{ fontSize: 11, color: 'var(--amber)' }}>
                  Not yet in your budget
                </span>
              )}
              <button className={`btn btn-sm${unsaved ? ' btn-primary' : ''}`}
                onClick={handleSaveAll} disabled={saving}
                title={unsaved
                  ? 'Write these amounts into the Budget tab'
                  : 'Budget is up to date with this plan'}>
                {saving ? 'Saving…' : unsaved ? 'Save all *' : 'Save all'}
              </button>
            </div>
          </div>

          {members.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
              No household members yet — add them in the <strong>Setup</strong> tab to plan income and deductions.
            </div>
          )}

          <table className="tbl">
            <thead>
              <tr><th>Source</th><th className="r">Gross</th><th className="r">Ded %</th><th className="r">Deduction</th><th className="r">Net</th><th style={{ width: 28 }}></th></tr>
            </thead>
            <tbody>
              {incomes.map(r => {
                const member = members.find(m => m.id === r.memberId);
                const ded = member ? (parseFloat(r.amount) || 0) * (member.deductionPct || 0) : 0;
                return (
                  <tr key={r.id ?? r.label}>
                    <td>{r.label}</td>
                    <td className="r" style={{ fontSize: 13 }}>{fmt(parseFloat(r.amount) || 0)}</td>
                    <td className="r">
                      {member ? (
                        <span style={{ fontSize: 12 }} title="Edit deduction % in the Setup tab">
                          {((member.deductionPct || 0) * 100).toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="r neg">{member ? fmt(-ded) : '—'}</td>
                    <td className="r">{fmt((parseFloat(r.amount) || 0) - ded)}</td>
                    <td></td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td><strong>Total</strong></td>
                <td className="r"><strong>{fmt(grossIncome)}</strong></td>
                <td className="r"><strong>{pct(grossIncome > 0 ? strat.totalDed / grossIncome : 0)}</strong></td>
                <td className="r neg"><strong>{fmt(-strat.totalDed)}</strong></td>
                <td className="r"><strong>{fmt(strat.netIncome)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--text3)' }}>
            Income comes from each member's annual salary in <strong>Setup</strong> — one place, so
            this tab and Setup can never disagree. Change it there and every month follows.
          </div>
        </div>

        {/* Balance summary */}
        <div className="card">
          <div className="sec-label">Balance</div>
          <table className="tbl">
            <tbody>
              <tr><td>Net income</td><td className="r pos">{fmt(strat.netIncome)}</td></tr>
              <tr><td>Fixed</td><td className="r neg">{fmt(-fixedTotal)}</td></tr>
              <tr><td>Variable goals</td><td className="r neg">{fmt(-strat.variableGoalSum)}</td></tr>
              <tr><td>Debt goals</td><td className="r neg">{fmt(-strat.debtGoalSum)}</td></tr>
              <tr><td>Savings goals</td><td className="r neg">{fmt(-strat.savingsGoalSum)}</td></tr>
              <tr className="total-row">
                <td><strong>{strat.deficit > 0 ? 'Shortfall' : 'Surplus'}</strong></td>
                <td className={`r ${strat.deficit > 0 ? 'neg' : 'pos'}`}>
                  <strong>{strat.deficit > 0 ? fmt(-strat.deficit) : fmt(Math.abs(strat.balance))}</strong>
                </td>
              </tr>
              {/* The 65 / 16 / 19 shortfall-split attribution row used to sit
                  here. The per-category cut amounts below stay — they show what
                  is actually being reduced — but how the shortfall is
                  apportioned is an internal detail, not a user-facing knob. */}
              {strat.deficit > 0 && (
                <>
                  <tr><td>Variable cut ({pct(strat.variableCut)})</td><td className="r neg">{fmt(-strat.variableGoalSum * strat.variableCut)}</td></tr>
                  {strat.debtCut > 0 && <tr><td>Debt cut ({pct(strat.debtCut)})</td><td className="r neg">{fmt(-strat.debtGoalSum * strat.debtCut)}</td></tr>}
                  {strat.savingsCut > 0 && <tr><td>Savings cut ({pct(strat.savingsCut)})</td><td className="r neg">{fmt(-strat.savingsGoalSum * strat.savingsCut)}</td></tr>}
                </>
              )}
              <tr className="total-row">
                <td><strong>After cuts</strong></td>
                <td className={`r ${strat.balance >= -0.01 ? 'pos' : 'neg'}`}>
                  <strong>{fmt(strat.balance)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Goals checklist */}
        <div className="card">
          <div className="sec-label">Goals</div>
          <ol style={{ listStyle: 'none', marginBottom: 14 }}>
            {goals.map((g, i) => (
              <li key={g.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'var(--surface2)', border: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginTop: 1 }}>{i + 1}</span>
                {editingGoalId === g.id ? (
                  <input
                    className="field-input"
                    style={{ flex: 1, fontSize: 13, padding: '2px 6px' }}
                    value={editingText}
                    onChange={e => setEditingText(e.target.value)}
                    onBlur={() => handleSaveGoalEdit(g)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveGoalEdit(g);
                      if (e.key === 'Escape') setEditingGoalId(null);
                    }}
                    autoFocus
                  />
                ) : (
                  <span
                    style={{ flex: 1, fontSize: 13, lineHeight: 1.5, textDecoration: g.completed ? 'line-through' : 'none', color: g.completed ? 'var(--text3)' : 'var(--text)', cursor: 'text' }}
                    onClick={() => { setEditingGoalId(g.id); setEditingText(g.description); }}
                  >{g.description}</span>
                )}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button className="btn btn-sm" onClick={() => handleTogGoal(g)}>{g.completed ? '↩' : '✓'}</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelGoal(g.id)}>✕</button>
                </div>
              </li>
            ))}
            {goals.length === 0 && <li style={{ color: 'var(--text3)', padding: '10px 0', fontSize: 13 }}>No goals yet.</li>}
          </ol>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="field-input" style={{ flex: 1 }} placeholder="Add a goal…"
              value={newGoal} onChange={e => setNewGoal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddGoal()} />
            <button className="btn btn-primary" onClick={handleAddGoal}>Add</button>
          </div>
        </div>

        {/* Debt summary */}
        <div className="card">
          <div className="sec-label">Debts</div>
          <table className="tbl">
            <tbody>
              {loanAccts.filter(a => Math.abs(a.balance) > 0.005).map(a => (
                <tr key={a.id}><td>{a.name}</td><td className="r neg">{fmt(-Math.abs(a.balance))}</td></tr>
              ))}
              <tr className="total-row">
                <td><strong>Total</strong></td>
                <td className="r neg"><strong>{fmt(totalDebtBalance)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── RIGHT ────────────────────────────────────────────── */}
      <div>
        <div className="card">
          <div style={{ marginBottom: 12 }}>
            <div className="sec-label" style={{ margin: 0 }}>Monthly Plan</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
              Savings and debt amounts come from Setup · Cuts apply when goals exceed net income · Planned feeds the Budget tab
            </div>
          </div>

          <table className="tbl">
            <thead>
              <tr>
                <th>Category</th>
                <th className="r">Goal</th>
                <th className="r">Cut</th>
                <th className="r">Planned</th>
              </tr>
            </thead>
            <tbody>

              {/* FIXED / OTHER MONTHLY */}
              {groupHdr('Other monthly — no cut')}
              {fixedGroupKeys.map(gk => (
                <React.Fragment key={gk}>
                  <tr>
                    <td colSpan={4} style={{ paddingLeft: 16, paddingTop: 8, fontSize: 10, fontWeight: 700,
                      color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{gk}</td>
                  </tr>
                  {fixedByGroup[gk].map(c => {
                    const val = getFixedPlanned(c);
                    const numVal = parseFloat(val) || 0;
                    return (
                      <tr key={c.id}>
                        <td style={{ paddingLeft: 28 }}>{c.name}</td>
                        <td className="r"><InlineNum value={val} onChange={v => handleFixedEdit(c.id, v)} /></td>
                        <td className="r muted">—</td>
                        <td className="r"><strong>{fmt(numVal)}</strong></td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
              {fixedCats.length === 0 && (
                <tr><td colSpan={4} style={{ paddingLeft: 16, fontSize: 12, color: 'var(--text3)' }}>
                  No other monthly categories — add them in the Budget tab.
                </td></tr>
              )}

              {/* VARIABLE */}
              {groupHdr(`Variable — ${pct(strat.variableCut)} cut`)}
              {spending.map(g => {
                const amt = parseFloat(g.monthlyAmount) || 0;
                const scaledPlanned = amt * varScale * vc;
                return (
                  <tr key={g.id}>
                    <td style={{ paddingLeft: 16 }}>
                      {g.name}
                      {varScale > 1.001 && (
                        <div style={{ fontSize: 10, color: 'var(--green)' }}>+savings overflow absorbed</div>
                      )}
                      {!g.linkedCategoryName && (
                        <div style={{ fontSize: 10, color: 'var(--text3)' }}>not linked to a budget category</div>
                      )}
                    </td>
                    <td className="r"><InlineNum value={g.monthlyAmount} onChange={v => editSpending(g.id, v)} /></td>
                    <td className={`r ${strat.variableCut > 0 ? 'neg' : 'muted'}`}>{pct(strat.variableCut)}</td>
                    <td className="r"><strong>{fmt(scaledPlanned)}</strong></td>
                  </tr>
                );
              })}
              {spending.length === 0 && (
                <tr><td colSpan={4} style={{ paddingLeft: 16, fontSize: 12, color: 'var(--text3)' }}>
                  Add variable spending goals in the Setup tab.
                </td></tr>
              )}
              {conflictedCats.length > 0 && (
                <tr><td colSpan={4} style={{ paddingLeft: 16, fontSize: 12, color: 'var(--amber)' }}>
                  {conflictedCats.map(c => c.name).join(', ')}{' '}
                  {conflictedCats.length === 1 ? 'is claimed' : 'are claimed'} by more than one thing
                  in Setup. Each envelope can only follow one — unlink the extras so its Planned
                  amount is predictable.
                </td></tr>
              )}

              {/* DEBT */}
              {groupHdr(`Debt — ${pct(strat.debtCut)} cut`)}
              {debts.map(p => {
                const base      = debtBase(p);
                const extraOnly = debtExtra(p);
                const effAmt    = effDebtExtraByPlanId[p.id] || 0;
                const rawGoal   = debtToggleAmt(p);
                const planned   = debtPlanned(p);
                const hasDate   = !!p.payoffGoalDate;
                return (
                  <tr key={p.id} style={{ opacity: p.enabled ? 1 : (allSavingsEffOff ? 0.4 : 0.55) }}>
                    <td style={{ paddingLeft: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <ToggleBtn on={!!p.enabled} onClick={() => toggleDebt(p)} style={{ marginTop: 2 }} />
                        <span>
                          {p.name}
                          {p.toggleFullPayment ? (
                            // Full-payment toggle (student-loan style)
                            effAmt > 0.01 ? (
                              extraOnly > 0.01 ? (
                                <>
                                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>base {fmt(base)} + {fmt(extraOnly)} extra</div>
                                  {allSavingsEffOff && effAmt > rawGoal + 0.01
                                    ? <div style={{ fontSize: 10, color: 'var(--green)' }}>+{fmt(effAmt - rawGoal)} savings overflow absorbed</div>
                                    : <div style={{ fontSize: 10, color: 'var(--blue)' }}>from payoff calculator · debt cut applies</div>}
                                </>
                              ) : (
                                <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                                  {hasDate ? 'minimum payment' : 'minimum payment · set a payoff date in Goals tab for extra'}
                                </div>
                              )
                            ) : (
                              <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                                {!allSavingsEffOff
                                  ? 'disabled — redistributed to savings'
                                  : 'disabled — overflow to active debt or variable'}
                              </div>
                            )
                          ) : (
                            // Extra-only toggle (mortgage style) — base always budgeted
                            effAmt > 0.01 ? (
                              <>
                                <div style={{ fontSize: 10, color: 'var(--text3)' }}>base {fmt(base)} + {fmt(effAmt)} extra</div>
                                {allSavingsEffOff && effAmt > rawGoal + 0.01
                                  ? <div style={{ fontSize: 10, color: 'var(--green)' }}>+{fmt(effAmt - rawGoal)} savings overflow absorbed</div>
                                  : <div style={{ fontSize: 10, color: 'var(--blue)' }}>from payoff calculator · cut on extra only</div>}
                              </>
                            ) : (
                              <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                                {!p.enabled && !allSavingsEffOff
                                  ? 'extra disabled — redistributed to savings'
                                  : !p.enabled && allSavingsEffOff
                                  ? 'base only — overflow sent to variable'
                                  : hasDate ? 'base only' : 'base only · set a payoff date in Goals tab for extra'}
                              </div>
                            )
                          )}
                          {!p.linkedCategoryName && (
                            <div style={{ fontSize: 10, color: 'var(--red)' }}>not linked to a budget category — Setup tab</div>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="r">
                      {p.toggleFullPayment ? (
                        effAmt > 0.01
                          ? extraOnly > 0.01
                            ? <><div>{fmt(effAmt)}</div><div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'right' }}>{fmt(base)} base</div></>
                            : <span style={{ fontSize: 12, color: 'var(--text3)' }}>{fmt(base)}</span>
                          : <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
                      ) : (
                        effAmt > 0.01
                          ? <><div>{fmt(base + effAmt)}</div><div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'right' }}>{fmt(base)} base</div></>
                          : <span style={{ fontSize: 12, color: 'var(--text3)' }}>{fmt(base)}</span>
                      )}
                    </td>
                    <td className={`r ${effAmt > 0.01 && strat.debtCut > 0 ? 'neg' : 'muted'}`}>
                      {effAmt > 0.01 ? pct(strat.debtCut) : '—'}
                    </td>
                    <td className="r"><strong>{fmt(planned)}</strong></td>
                  </tr>
                );
              })}
              {debts.length === 0 && (
                <tr><td colSpan={4} style={{ paddingLeft: 16, fontSize: 12, color: 'var(--text3)' }}>
                  Add debt payoff plans in the Setup tab.
                </td></tr>
              )}

              {/* SAVINGS */}
              <tr style={{ background: 'var(--surface2)', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <td style={{ padding: '6px 10px' }}>{`Savings — ${pct(strat.savingsCut)} cut`}</td>
                <td colSpan={3} style={{ textAlign: 'right', padding: '4px 10px' }}>
                  {savings.length > 0 && (
                    <button
                      onClick={toggleAllSavings}
                      title={allSavingsOn ? 'Disable all savings' : 'Enable all savings'}
                      style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 99,
                        border: `1px solid ${allSavingsOn ? 'var(--green)' : 'var(--border2)'}`,
                        background: allSavingsOn ? 'var(--green-bg)' : 'transparent',
                        color: allSavingsOn ? 'var(--green)' : 'var(--text3)',
                        cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}
                    >
                      {allSavingsOn ? 'ALL ON' : 'ALL OFF'}
                    </button>
                  )}
                </td>
              </tr>
              {savings.map(p => {
                const base   = baseOf(p);
                const effVal = effSavingsByPlanId[p.id] || 0;
                const isOn   = !!p.enabled;
                const absorbed = isOn && effVal > base + 0.01 ? effVal - base : 0;
                return (
                  <tr key={p.id} style={{ opacity: isOn ? 1 : 0.45 }}>
                    <td style={{ paddingLeft: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ToggleBtn on={isOn} onClick={() => toggleSavings(p)} />
                        <span>
                          {p.name}
                          {isOn && base > 0 && (
                            <div style={{ fontSize: 10, color: 'var(--blue)' }}>from Setup</div>
                          )}
                          {absorbed > 0.01 && (
                            <div style={{ fontSize: 10, color: 'var(--green)' }}>+{fmt(absorbed)} absorbed</div>
                          )}
                          {!p.linkedCategoryName && (
                            <div style={{ fontSize: 10, color: 'var(--red)' }}>not linked to a budget category — Setup tab</div>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="r">
                      <span style={{ fontSize: 12, color: isOn ? undefined : 'var(--text3)' }}
                        title="Edit this plan's details in Setup">{fmt(base)}</span>
                    </td>
                    <td className={`r ${isOn && strat.savingsCut > 0 ? 'neg' : 'muted'}`}>
                      {isOn ? pct(strat.savingsCut) : '—'}
                    </td>
                    <td className="r"><strong>{fmt(isOn ? effVal * sc : 0)}</strong></td>
                  </tr>
                );
              })}
              {savings.length === 0 && (
                <tr><td colSpan={4} style={{ paddingLeft: 16, fontSize: 12, color: 'var(--text3)' }}>
                  Add savings plans in the Setup tab.
                </td></tr>
              )}

              {/* TOTAL */}
              <tr className="total-row">
                <td><strong>Total</strong></td>
                <td className="r muted"><strong>{fmt(strat.totalGoal)}</strong></td>
                <td></td>
                <td className="r">
                  <strong>{fmt(
                    fixedTotal
                    + debts.reduce((s, p) => s + (effDebtExtraByPlanId[p.id] || 0) * dc, 0)
                    + strat.variableGoalSum * vc
                    + strat.savingsGoalSum  * sc
                  )}</strong>
                </td>
              </tr>

            </tbody>
          </table>
        </div>

        {/* 401k payroll deductions */}
        {k401kPlans.length > 0 && (
          <div className="card">
            <div className="sec-label">401k Payroll Deductions</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
              Effective monthly contributions after toggle state &amp; cuts — reflects whose income is reduced
            </div>
            <table className="tbl">
              <thead>
                <tr><th>Plan</th><th className="r">Amount / mo</th><th className="r">% of gross</th></tr>
              </thead>
              <tbody>
                {k401kPlans.map(p => {
                  const isOn = !!p.enabled;
                  const effVal = effSavingsByPlanId[p.id] || 0;
                  const amount = effVal * sc;
                  const gross = p.ownerMemberId != null ? (incomeByMemberId[p.ownerMemberId] || 0) : 0;
                  const absorbed = isOn && effVal > baseOf(p) + 0.01 ? effVal - baseOf(p) : 0;
                  return (
                    <tr key={p.id} style={{ opacity: isOn ? 1 : 0.45 }}>
                      <td>
                        {p.name}
                        {!isOn
                          ? <div style={{ fontSize: 10, color: 'var(--text3)' }}>OFF — amount redistributed</div>
                          : absorbed > 0.01
                          ? <div style={{ fontSize: 10, color: 'var(--green)' }}>+{fmt(absorbed)} absorbed</div>
                          : null}
                      </td>
                      <td className="r">{isOn ? fmt(amount) : <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                      <td className="r muted">{isOn && gross > 0 ? pct(amount / gross) : '—'}</td>
                    </tr>
                  );
                })}
                <tr className="total-row">
                  <td><strong>Total 401k / mo</strong></td>
                  <td className="r"><strong>{fmt(k401kPlans.reduce((s, p) => s + (effSavingsByPlanId[p.id] || 0), 0) * sc)}</strong></td>
                  <td className="r muted"><strong>{grossIncome > 0 ? pct(k401kPlans.reduce((s, p) => s + (effSavingsByPlanId[p.id] || 0), 0) * sc / grossIncome) : '—'}</strong></td>
                </tr>
                <tr>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>Retirement balance</td>
                  <td className="r pos" colSpan={2}>
                    {fmt(savingsResolved.filter(p => ['RETIREMENT_401K','IRA','BROKERAGE'].includes(p.type)).reduce((s, p) => s + (p.balance || 0), 0))}
                    {' of '}{fmt(settings.sharedRetirementGoal || 2000000)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>

    {/* Projections / Forecast — read-only retirement, goal & debt outlook (was the Goals tab) */}
    <div className="card" style={{ marginTop: 16 }}>
      <button
        onClick={() => setShowProjections(v => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
        <span className="sec-label" style={{ margin: 0 }}>Projections / Forecast</span>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{showProjections ? 'Hide ▲' : 'Show ▼'}</span>
      </button>
      {showProjections && (
        <div style={{ marginTop: 14 }}>
          <ProjectionsPanel members={members} savings={savingsResolved} debts={debtsResolved} settings={settings} incomes={incomes} />
        </div>
      )}
    </div>
    </>
  );
}
